import { router, usePage } from "@inertiajs/react";
import { useCallback, useEffect, useMemo } from "react";

interface UseAdminTabsOptions {
  basePath: string;
  validTabs: ReadonlySet<string>;
  defaultTab: string;
  aliases?: Record<string, string>;
}

interface TabApi {
  initialTab: string;
  onSelectionChange: (key: React.Key) => void;
}

export function useAdminTabs({ basePath, validTabs, defaultTab, aliases = {} }: UseAdminTabsOptions): TabApi {
  const page = usePage();

  const resolveTab = useCallback(
    (url: string): string => {
      const raw = new URLSearchParams(url.split("?")[1] ?? "").get("tab") ?? defaultTab;
      if (aliases[raw]) return aliases[raw];
      return validTabs.has(raw) ? raw : defaultTab;
    },
    [aliases, defaultTab, validTabs],
  );

  const initialTab = useMemo(() => resolveTab(page.url), [page.url, resolveTab]);

  useEffect(() => {
    const params = new URLSearchParams(page.url.split("?")[1] ?? "");
    const raw = params.get("tab");
    if (raw && aliases[raw]) {
      params.set("tab", aliases[raw]);
      router.replace({ url: `${basePath}?${params.toString()}` });
    }
  }, [aliases, basePath, page.url]);

  const onSelectionChange = useCallback(
    (key: React.Key) => {
      router.get(basePath, { tab: String(key) }, { preserveState: true, preserveScroll: true });
    },
    [basePath],
  );

  return { initialTab, onSelectionChange };
}
